import React from "react";
import type { Position, Player } from "../../shared/models/Player";
import { DndContext, DragEndEvent, DragOverlay, DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, rectSortingStrategy } from "@dnd-kit/sortable";
import { BoardCell, CellContent } from "../../shared/components/BoardCell";
import Cheatsheet from "../../shared/components/Cheatsheet";
import CompareRankingsView, { type CompareRankingsColumn } from "../../shared/components/CompareRankingsView";
import TeamLogo from "../../shared/components/TeamLogo";
import TeamsBoard, { type StarterConfig, type TeamOverridesState } from "../../shared/components/TeamsBoard";
import type { TiersByPos } from "../../shared/utils/xlsxRankings";
import { formatTeamAbbreviation } from "../../shared/utils/teamAbbreviation";
import {
  PlannerFavoriteCard,
  PlannerRoundLane,
} from "../features/desktop/plannerComponents";
import { MobileRankingsBoardCard } from "../features/mobile/mobileRankingsBoardCard";
import {
  arraysEqual,
  getRankedRoundForPlayer,
  isReverseRound,
  normalizeDraftAssignSearch,
  numberToWords,
} from "../features/shared/boardUtils";
import type { BoardTab, DraftStyle } from "./boardTypes";

export type { BoardTab, DraftStyle } from "./boardTypes";
const CHEATSHEET_POSITION_ORDER: Position[] = ["QB", "RB", "WR", "TE", "K", "DST"];
const DEFAULT_CHEATSHEET_VISIBLE_POSITIONS: Record<Position, boolean> = {
  QB: true,
  RB: true,
  WR: true,
  TE: true,
  K: true,
  DST: true,
};

const BOARD_BG_URL = `${process.env.PUBLIC_URL || ""}/bg.jpg`;

function Board(props: {
  favoriteIds: Set<string>;
  plannerRoundById: Record<string, number>;
  setPlannerRoundById: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  plannerOrderedIds: string[];
  setPlannerOrderedIds: React.Dispatch<React.SetStateAction<string[]>>;

  // Only the user Rankings list should be reorderable.
  allowRankingsReorder: boolean;

  boardTab: BoardTab;
  setBoardTab: (t: BoardTab) => void;

  rounds: number;
  setRounds: React.Dispatch<React.SetStateAction<number>>;

  teams: number;
  setTeams: React.Dispatch<React.SetStateAction<number>>;

  onAddPlayer: (name: string, position: Position) => void;
  draftStyle: DraftStyle;
  setDraftStyle: (s: DraftStyle) => void;

  rankingIds: string[];

  playersById: Record<string, Player>;
  tiersByPos: TiersByPos;
  onUpdateTiersByPos: (pos: Position, tierBreaks: string[]) => void;

  draftedIds: Set<string>;
  onToggleDrafted: (id: string) => void;
  onToggleFavorite?: (id: string) => void;
  clearAllDrafted: () => void;

  teamNames: string[];
  setTeamNames: React.Dispatch<React.SetStateAction<string[]>>;

  draftSlots: (string | null)[];
  draftSlotTeamIndexes?: Array<number | null>;
  rosterStarterConfig: StarterConfig;
  setRosterStarterConfig: React.Dispatch<React.SetStateAction<StarterConfig>>;
  rosterTeamOverrides: TeamOverridesState;
  setRosterTeamOverrides: React.Dispatch<React.SetStateAction<TeamOverridesState>>;

  posColor: (pos: Position) => string;

  sensors: any;
  onBoardDragEnd: (event: DragEndEvent) => void;
  onDraftBoardDragEnd: (event: DragEndEvent) => void;
  onAssignPlayerToDraftSlot: (slotIndex: number, playerId: string) => void;
  compareColumns?: CompareRankingsColumn[];
  onImportCompareRankings?: () => void;
  onReorderCompareColumns?: (fromId: string, toId: string) => void;
  onMoveMyCompareRankings?: (fromId: string, toId: string) => void;
  onRemoveCompareColumn?: (columnId: string) => void;
  availableTabs?: BoardTab[];
  mobileMode?: boolean;
  tabletMode?: boolean;
  tabletScale?: number;
  boardZoom?: number;
  mobileTopClearance?: number;
  allowDraftBoardReorder?: boolean;
  showTabSwitcher?: boolean;
  onOpenDraftSync?: () => void;
  onRefreshDraftSync?: () => void;
  isRefreshingDraftSync?: boolean;
  hideKickers?: boolean;
  hideDefenses?: boolean;
  uiScale?: number;
  mobileCheatsheetViewOpen?: boolean;
  setMobileCheatsheetViewOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  mobileCheatsheetVisiblePositions?: Record<Position, boolean>;
  setMobileCheatsheetVisiblePositions?: React.Dispatch<React.SetStateAction<Record<Position, boolean>>>;
  mobileTeamsViewOpen?: boolean;
  setMobileTeamsViewOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenPlayerProfile?: (id: string) => void;
  shouldSuppressMobileBoardTap?: () => boolean;
}) {
  const {
    favoriteIds,
    plannerRoundById,
    setPlannerRoundById,
    plannerOrderedIds,
    setPlannerOrderedIds,
    allowRankingsReorder,
    boardTab,
    setBoardTab,
    rounds,
    setRounds,
    teams,
    setTeams,
    onAddPlayer,
    draftStyle,
    setDraftStyle,
    rankingIds,
    playersById,
    tiersByPos,
    onUpdateTiersByPos,
    draftedIds,
    onToggleDrafted,
    onToggleFavorite,
    clearAllDrafted,
    teamNames,
    setTeamNames,
    draftSlots,
    draftSlotTeamIndexes,
    rosterStarterConfig,
    setRosterStarterConfig,
    rosterTeamOverrides,
    setRosterTeamOverrides,
    posColor,
    sensors,
    onBoardDragEnd,
    onDraftBoardDragEnd,
    onAssignPlayerToDraftSlot,
    compareColumns = [],
    onImportCompareRankings,
    onReorderCompareColumns,
    onMoveMyCompareRankings,
    onRemoveCompareColumn,
    availableTabs,
    mobileMode = false,
    tabletMode = false,
    tabletScale = 1,
    boardZoom = 1,
    mobileTopClearance = 0,
    allowDraftBoardReorder = true,
    showTabSwitcher = true,
    onOpenDraftSync,
    onRefreshDraftSync,
    isRefreshingDraftSync = false,
    hideKickers = false,
    hideDefenses = false,
    uiScale = 1,
    mobileCheatsheetViewOpen: mobileCheatsheetViewOpenProp,
    setMobileCheatsheetViewOpen: setMobileCheatsheetViewOpenProp,
    mobileCheatsheetVisiblePositions: mobileCheatsheetVisiblePositionsProp,
    setMobileCheatsheetVisiblePositions: setMobileCheatsheetVisiblePositionsProp,
    mobileTeamsViewOpen: mobileTeamsViewOpenProp,
    setMobileTeamsViewOpen: setMobileTeamsViewOpenProp,
    onOpenPlayerProfile,
    shouldSuppressMobileBoardTap,
  } = props;

  // Cheatsheet should reflect the active rankings source.
  const cheatsheetSourceIds = rankingIds;
  const cheatsheetRankingIds = React.useMemo(() => {
    return cheatsheetSourceIds.filter((id) => {
      const player = playersById[id];
      if (!player) return false;
      if (hideKickers && player.position === "K") return false;
      if (hideDefenses && player.position === "DST") return false;
      return true;
    });
  }, [cheatsheetSourceIds, playersById, hideKickers, hideDefenses]);
  const cheatsheetTiersByPos = tiersByPos;
  const cheatsheetOnUpdateTiersByPos = onUpdateTiersByPos;
  const cheatsheetPresentPositions = React.useMemo(() => {
    const present = new Set<Position>();
    cheatsheetRankingIds.forEach((id) => {
      const player = playersById[id];
      if (player) present.add(player.position);
    });
    return CHEATSHEET_POSITION_ORDER.filter((pos) => present.has(pos));
  }, [cheatsheetRankingIds, playersById]);

  // Keep track of the last non-cheatsheet board mode so the toggle can return you to it.
  const lastNonCheatsheetRef = React.useRef<BoardTab>("Rankings Board");
  if (boardTab !== "Cheatsheet") lastNonCheatsheetRef.current = boardTab;

  const [newPlayerName, setNewPlayerName] = React.useState<string>("");
  const [newPlayerPos, setNewPlayerPos] = React.useState<Position>("RB");
  const [desktopSettingsOpen, setDesktopSettingsOpen] = React.useState(false);
  const [confirmRestartDraftOpen, setConfirmRestartDraftOpen] = React.useState(false);
  const [mobileCheatsheetViewOpenInternal, setMobileCheatsheetViewOpenInternal] = React.useState(false);
  const [mobileCheatsheetVisiblePositionsInternal, setMobileCheatsheetVisiblePositionsInternal] = React.useState<Record<Position, boolean>>(
    DEFAULT_CHEATSHEET_VISIBLE_POSITIONS
  );
  const [mobileTeamsViewOpenInternal, setMobileTeamsViewOpenInternal] = React.useState(false);
  const desktopSettingsRef = React.useRef<HTMLDivElement | null>(null);
  const mobileCheatsheetViewOpen = mobileCheatsheetViewOpenProp ?? mobileCheatsheetViewOpenInternal;
  const setMobileCheatsheetViewOpen = setMobileCheatsheetViewOpenProp ?? setMobileCheatsheetViewOpenInternal;
  const mobileCheatsheetVisiblePositions = mobileCheatsheetVisiblePositionsProp ?? mobileCheatsheetVisiblePositionsInternal;
  const setMobileCheatsheetVisiblePositions =
    setMobileCheatsheetVisiblePositionsProp ?? setMobileCheatsheetVisiblePositionsInternal;
  const mobileTeamsViewOpen = mobileTeamsViewOpenProp ?? mobileTeamsViewOpenInternal;
  const setMobileTeamsViewOpen = setMobileTeamsViewOpenProp ?? setMobileTeamsViewOpenInternal;
  const cheatsheetVisiblePositions = React.useMemo(
    () => cheatsheetPresentPositions.filter((pos) => mobileCheatsheetVisiblePositions[pos]),
    [cheatsheetPresentPositions, mobileCheatsheetVisiblePositions]
  );


// Draft-board: click an empty cell -> menu -> assign a player to that pick.
const [draftAssignMenu, setDraftAssignMenu] = React.useState<null | { slotIndex: number; x: number; y: number; label: string }>(null);
const [draftRemoveMenu, setDraftRemoveMenu] = React.useState<null | { slotIndex: number; x: number; y: number; label: string; playerId: string; playerName: string }>(null);
const [draftAssignModal, setDraftAssignModal] = React.useState<null | { slotIndex: number; label: string }>(null);
const [draftAssignQuery, setDraftAssignQuery] = React.useState<string>("");
const [mobileDraftAssignPrompt, setMobileDraftAssignPrompt] = React.useState<null | { slotIndex: number; label: string }>(null);
const [mobileDraftAssignPosition, setMobileDraftAssignPosition] = React.useState<"ALL" | Position>("ALL");
const [plannerAssignModal, setPlannerAssignModal] = React.useState<null | { round: number; label: string }>(null);
const [plannerAssignQuery, setPlannerAssignQuery] = React.useState<string>("");

  const draftAssignInputRef = React.useRef<HTMLInputElement | null>(null);
  const plannerAssignInputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (!(mobileMode && boardTab === "Cheatsheet")) setMobileCheatsheetViewOpen(false);
  }, [mobileMode, boardTab, setMobileCheatsheetViewOpen]);
  React.useEffect(() => {
    if (mobileMode && boardTab === "Compare Rankings") setBoardTab("Rankings Board");
  }, [mobileMode, boardTab, setBoardTab]);
  React.useEffect(() => {
    if (!(mobileMode && boardTab === "Teams")) setMobileTeamsViewOpen(false);
  }, [mobileMode, boardTab, setMobileTeamsViewOpen]);
  React.useEffect(() => {
    if (!draftAssignModal) return;
    const raf = window.requestAnimationFrame(() => {
      draftAssignInputRef.current?.focus();
      draftAssignInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [draftAssignModal]);
  React.useEffect(() => {
    if (!(mobileMode && boardTab === "Draft Board")) {
      setMobileDraftAssignPrompt(null);
      setDraftAssignModal(null);
    }
  }, [mobileMode, boardTab]);

  React.useEffect(() => {
    if (!plannerAssignModal) return;
    const raf = window.requestAnimationFrame(() => {
      plannerAssignInputRef.current?.focus();
      plannerAssignInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [plannerAssignModal]);


  const draftAssignCandidates = React.useMemo<string[]>(() => {
    if (!draftAssignModal) return [];
    const q = normalizeDraftAssignSearch(draftAssignQuery);

    const undraftedIds = Object.keys(playersById).filter((id) => !draftedIds.has(id));

    if (!q) {
      const rankedUndrafted = rankingIds.filter((id) => !draftedIds.has(id) && playersById[id]);
      const rankedSet = new Set(rankedUndrafted);
      const extras = undraftedIds
        .filter((id) => !rankedSet.has(id))
        .sort((a, b) => {
          const pa = playersById[a];
          const pb = playersById[b];
          return (pa?.name ?? "").localeCompare(pb?.name ?? "");
        });
      return [...rankedUndrafted, ...extras];
    }

    const filtered = undraftedIds.filter((id) => {
      const p = playersById[id];
      if (!p) return false;
      const name = normalizeDraftAssignSearch(p.name ?? "");
      const pos = normalizeDraftAssignSearch((p as any).pos ?? "");
      const team = normalizeDraftAssignSearch((p as any).team ?? "");
      return name.includes(q) || pos.includes(q) || team.includes(q);
    });

    // Stable-ish alphabetical ordering for the modal list.
    filtered.sort((a, b) => {
      const pa = playersById[a];
      const pb = playersById[b];
      return (pa?.name ?? "").localeCompare(pb?.name ?? "");
    });

    return filtered;
  }, [draftAssignModal, draftAssignQuery, draftedIds, playersById, rankingIds]);
  const mobileDraftAssignBaseIds = React.useMemo<string[]>(() => {
    if (!(mobileMode && draftAssignModal)) return [];
    return rankingIds.filter((id) => !draftedIds.has(id) && playersById[id]);
  }, [mobileMode, draftAssignModal, rankingIds, draftedIds, playersById]);
  const mobileDraftAssignPositionTabs = React.useMemo(() => {
    const present = new Set<Position>();
    mobileDraftAssignBaseIds.forEach((id) => {
      const player = playersById[id];
      if (player) present.add(player.position);
    });
    const ordered: Array<"ALL" | Position> = ["ALL", "QB", "RB", "WR", "TE"];
    if (present.has("K")) ordered.push("K");
    if (present.has("DST")) ordered.push("DST");
    return ordered;
  }, [mobileDraftAssignBaseIds, playersById]);
  const mobileDraftAssignCandidates = React.useMemo(() => {
    if (!(mobileMode && draftAssignModal)) return [];
    const q = normalizeDraftAssignSearch(draftAssignQuery);
    return mobileDraftAssignBaseIds.filter((id) => {
      const player = playersById[id];
      if (!player) return false;
      if (mobileDraftAssignPosition !== "ALL" && player.position !== mobileDraftAssignPosition) return false;
      if (!q) return true;
      const name = normalizeDraftAssignSearch(player.name ?? "");
      const pos = normalizeDraftAssignSearch(player.position ?? "");
      const team = normalizeDraftAssignSearch(player.team ?? "");
      return name.includes(q) || pos.includes(q) || team.includes(q);
    });
  }, [mobileMode, draftAssignModal, draftAssignQuery, mobileDraftAssignBaseIds, mobileDraftAssignPosition, playersById]);

  const plannerAssignCandidates = React.useMemo<string[]>(() => {
    if (!plannerAssignModal) return [];
    const q = normalizeDraftAssignSearch(plannerAssignQuery);

    const ids = rankingIds.filter((id) => !favoriteIds.has(id));

    const filtered = !q
      ? ids
      : ids.filter((id) => {
          const p = playersById[id];
          if (!p) return false;
          const name = normalizeDraftAssignSearch(p.name ?? "");
          const pos = normalizeDraftAssignSearch((p as any).pos ?? "");
          const team = normalizeDraftAssignSearch((p as any).team ?? "");
          return name.includes(q) || pos.includes(q) || team.includes(q);
        });

    return filtered;
  }, [favoriteIds, plannerAssignModal, plannerAssignQuery, playersById, rankingIds]);



  const canAddPlayer = newPlayerName.trim().length > 0;
  const submitAddPlayer = React.useCallback(() => {
    const name = newPlayerName.trim();
    if (!name) return;
    onAddPlayer(name, newPlayerPos);
    setNewPlayerName("");
  }, [newPlayerName, newPlayerPos, onAddPlayer]);

  React.useEffect(() => {
    if (!desktopSettingsOpen || mobileMode) return undefined;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (desktopSettingsRef.current?.contains(target)) return;
      setDesktopSettingsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDesktopSettingsOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [desktopSettingsOpen, mobileMode]);

  React.useEffect(() => {
    if (!desktopSettingsOpen) {
      setConfirmRestartDraftOpen(false);
    }
  }, [desktopSettingsOpen]);

  const isCheat = boardTab === "Cheatsheet";
  const isTeams = boardTab === "Teams";
  const isDraft = boardTab === "Draft Board";
  const isPlanner = boardTab === "Draft Planner";
  const isRank = boardTab === "Rankings Board";
  const isCompare = boardTab === "Compare Rankings";
  const compactMobileMode = mobileMode && !tabletMode;
  const boardCellWidth = mobileMode ? (tabletMode ? 91 : 72) : 140;
  const boardCellMinWidth = mobileMode ? (tabletMode ? 91 : 72) : 140;
  const draftBoardCellMinHeight = mobileMode ? (tabletMode ? 64 : 48) : undefined;
  const boardCellRadius = mobileMode ? 7 : 16;
  const boardCellPadding = mobileMode ? (tabletMode ? 5 : 4) : 8;
  const boardCellMarginRight = mobileMode ? (tabletMode ? 2 : 1) : 4;
  const showBoardCellImages = tabletMode || !mobileMode;
  const visibleTabs = availableTabs ?? ["Rankings Board", "Draft Board", "Draft Planner", "Cheatsheet", "Teams", "Compare Rankings"];
  const showRankTab = visibleTabs.includes("Rankings Board");
  const showDraftTab = visibleTabs.includes("Draft Board");
  const showPlannerTab = visibleTabs.includes("Draft Planner");
  const showCheatsheetTab = visibleTabs.includes("Cheatsheet");
  const showTeamsTab = visibleTabs.includes("Teams");
  const showCompareTab = !mobileMode && visibleTabs.includes("Compare Rankings");

  const [activePlannerPlayerId, setActivePlannerPlayerId] = React.useState<string | null>(null);
  const plannerRoundByIdRef = React.useRef<Record<string, number>>({});
  const plannerOrderedIdsRef = React.useRef<string[]>([]);
  const mainScrollRef = React.useRef<HTMLDivElement | null>(null);
  const mainScrollTouchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const bottomScrollbarRef = React.useRef<HTMLDivElement | null>(null);
  const syncingScrollRef = React.useRef<"main" | "bottom" | null>(null);
  const [bottomScrollbarWidth, setBottomScrollbarWidth] = React.useState(0);
  const [showBottomScrollbar, setShowBottomScrollbar] = React.useState(false);

  React.useEffect(() => {
    plannerRoundByIdRef.current = plannerRoundById;
  }, [plannerRoundById]);

  React.useEffect(() => {
    plannerOrderedIdsRef.current = plannerOrderedIds;
  }, [plannerOrderedIds]);

  React.useEffect(() => {
    setPlannerRoundById((prev) => {
      const next: Record<string, number> = {};
      let changed = false;

      favoriteIds.forEach((playerId) => {
        const currentRound = prev[playerId];
        const defaultRound = getRankedRoundForPlayer(playerId, rankingIds, teams, rounds);
        const round = currentRound ? Math.min(rounds, Math.max(1, currentRound)) : defaultRound;
        next[playerId] = round;
        if (prev[playerId] !== round) changed = true;
      });

      if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
  }, [favoriteIds, rankingIds, rounds, setPlannerRoundById, teams]);

  React.useEffect(() => {
    setPlannerOrderedIds((prev) => {
      const next = rankingIds.filter((playerId) => favoriteIds.has(playerId));
      if (
        prev.length === next.length &&
        prev.every((playerId, index) => playerId === next[index])
      ) {
        return prev;
      }

      const remaining = prev.filter((playerId) => favoriteIds.has(playerId));
      const seen = new Set(remaining);
      const additions = next.filter((playerId) => !seen.has(playerId));
      return [...remaining, ...additions];
    });
  }, [favoriteIds, rankingIds, setPlannerOrderedIds]);

  const plannerRows = React.useMemo(() => {
    const rows = Array.from({ length: rounds }, () => [] as string[]);
    const favoriteInPlannerOrder = plannerOrderedIds.filter((playerId) => favoriteIds.has(playerId));

    favoriteInPlannerOrder.forEach((playerId) => {
      const round = plannerRoundById[playerId] ?? getRankedRoundForPlayer(playerId, rankingIds, teams, rounds);
      const rowIndex = Math.min(rounds, Math.max(1, round)) - 1;
      rows[rowIndex]?.push(playerId);
    });

    return rows;
  }, [favoriteIds, plannerOrderedIds, plannerRoundById, rankingIds, rounds, teams]);

  const currentDraftRound = React.useMemo(() => {
    const hasAnyDraftedPick = draftSlots.some((playerId) => playerId !== null);
    if (!hasAnyDraftedPick) return null;
    const nextPickIndex = draftSlots.findIndex((playerId) => playerId === null);
    if (nextPickIndex === -1 || teams <= 0) return null;
    return Math.floor(nextPickIndex / teams) + 1;
  }, [draftSlots, teams]);

  const movePlannerPlayer = React.useCallback((playerId: string, overId: string) => {
    if (!playerId || !overId) return;

    const getRoundForId = (id: string) => plannerRoundByIdRef.current[id] ?? getRankedRoundForPlayer(id, rankingIds, teams, rounds);

    const cardMatch = /^draft-planner:(.+)$/.exec(overId);
    if (cardMatch) {
      const targetId = cardMatch[1];
      if (!targetId || targetId === playerId) return;
      const nextRound = getRoundForId(targetId);

      setPlannerRoundById((prev) => {
        const next = prev[playerId] === nextRound ? prev : { ...prev, [playerId]: nextRound };
        plannerRoundByIdRef.current = next;
        return next;
      });

      setPlannerOrderedIds((prev) => {
        const withoutActive = prev.filter((id) => id !== playerId);
        const targetIndex = withoutActive.indexOf(targetId);
        if (targetIndex < 0) {
          plannerOrderedIdsRef.current = withoutActive;
          return withoutActive;
        }
        const next = [...withoutActive];
        next.splice(targetIndex, 0, playerId);
        if (arraysEqual(prev, next)) return prev;
        plannerOrderedIdsRef.current = next;
        return next;
      });
      return;
    }

    const roundMatch = /^draft-planner-round:(\d+)$/.exec(overId);
    if (!roundMatch) return;

    const nextRound = Number(roundMatch[1]);
    if (!Number.isFinite(nextRound)) return;

    setPlannerRoundById((prev) => {
      const next = prev[playerId] === nextRound ? prev : { ...prev, [playerId]: nextRound };
      plannerRoundByIdRef.current = next;
      return next;
    });

    setPlannerOrderedIds((prev) => {
      const withoutActive = prev.filter((id) => id !== playerId);
      const insertAfterIndex = withoutActive.reduce((lastIndex, id, index) => {
        return getRoundForId(id) === nextRound ? index : lastIndex;
      }, -1);
      const next = [...withoutActive];
      next.splice(insertAfterIndex + 1, 0, playerId);
      if (arraysEqual(prev, next)) return prev;
      plannerOrderedIdsRef.current = next;
      return next;
    });
  }, [rankingIds, rounds, setPlannerOrderedIds, setPlannerRoundById, teams]);

  const onPlannerDragStart = React.useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (!activeId.startsWith("draft-planner:")) return;
    setActivePlannerPlayerId(activeId.replace("draft-planner:", ""));
  }, []);

  const onPlannerDragOver = React.useCallback((event: DragOverEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : "";
    if (!activeId.startsWith("draft-planner:") || !overId) return;
    movePlannerPlayer(activeId.replace("draft-planner:", ""), overId);
  }, [movePlannerPlayer]);

  const onPlannerDragEnd = React.useCallback((event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : "";
    if (activeId.startsWith("draft-planner:") && overId) {
      movePlannerPlayer(activeId.replace("draft-planner:", ""), overId);
    }
    setActivePlannerPlayerId(null);
  }, [movePlannerPlayer]);

  const openPlannerAssignModal = React.useCallback((round: number) => {
    setPlannerAssignModal({ round, label: `Round ${numberToWords(round)}` });
    setPlannerAssignQuery("");
  }, []);

  const addPlayerToPlannerRound = React.useCallback((playerId: string, round: number) => {
    if (!playerId) return;
    if (onToggleFavorite && !favoriteIds.has(playerId)) {
      onToggleFavorite(playerId);
    }

    setPlannerRoundById((prev) => {
      const next = { ...prev, [playerId]: round };
      plannerRoundByIdRef.current = next;
      return next;
    });

    setPlannerOrderedIds((prev) => {
      const withoutPlayer = prev.filter((id) => id !== playerId);
      const insertAfterIndex = withoutPlayer.reduce((lastIndex, id, index) => {
        const idRound = plannerRoundByIdRef.current[id] ?? getRankedRoundForPlayer(id, rankingIds, teams, rounds);
        return idRound === round ? index : lastIndex;
      }, -1);
      const next = [...withoutPlayer];
      next.splice(insertAfterIndex + 1, 0, playerId);
      plannerOrderedIdsRef.current = next;
      return next;
    });
  }, [favoriteIds, onToggleFavorite, rankingIds, rounds, setPlannerOrderedIds, setPlannerRoundById, teams]);

  // Measure the non-cheatsheet board (Rankings/Draft) so Cheatsheet can reserve the same space.
  const tableSizeRef = React.useRef<HTMLDivElement | null>(null);
  const boardViewportRef = React.useRef<HTMLDivElement | null>(null);
  const teamNameInputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const [tableSize, setTableSize] = React.useState<{ width: number; height: number } | null>(null);
  const [boardContentScale, setBoardContentScale] = React.useState(1);

  const focusTeamNameInput = React.useCallback((teamIndex: number) => {
    const input = teamNameInputRefs.current[teamIndex];
    if (!input) return;
    input.focus({ preventScroll: true });
    input.select();
  }, []);

  React.useLayoutEffect(() => {
    const el = tableSizeRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      // Avoid thrashing state for sub-pixel changes
      const next = { width: Math.ceil(rect.width), height: Math.ceil(rect.height) };
      setTableSize((prev) => {
        if (prev && prev.width === next.width && prev.height === next.height) return prev;
        return next;
      });
    };

    update();

    // ResizeObserver is supported in modern browsers; if unavailable, gracefully do nothing beyond initial update.
    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    teams,
    rounds,
    draftStyle,
    // Re-measure when toggling between ADP and Draft since the header row differs.
    boardTab,
    // RankingIds changes can alter size (e.g., presence/absence of images may affect cell height).
    rankingIds,
    draftSlots,
  ]);

  React.useLayoutEffect(() => {
    if (mobileMode && !tabletMode) {
      setBoardContentScale(1.12);
      return;
    }

    if (!isRank && !isDraft && !isTeams) {
      setBoardContentScale(1);
      return;
    }

    const viewport = boardViewportRef.current;
    const content = tableSizeRef.current;
    if (!viewport || !content) return;

    const update = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const naturalWidth = content.scrollWidth;
      if (viewportRect.width <= 0 || naturalWidth <= 0) return;
      if (naturalWidth <= 0) return;

      const minScale = mobileMode ? 0.74 : 0.68;
      const maxScale = mobileMode ? 1.45 : 1.62;
      const widthPadding = mobileMode ? 2 : 4;
      const nextScale = Math.max(minScale, Math.min(maxScale, (viewportRect.width - widthPadding) / naturalWidth));

      setBoardContentScale((prev) => (Math.abs(prev - nextScale) < 0.01 ? prev : nextScale));
    };

    update();

    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => update());
    ro.observe(viewport);
    ro.observe(content);
    return () => ro.disconnect();
  }, [mobileMode, tabletMode, isRank, isDraft, isTeams, teams, rounds, draftStyle, rankingIds, draftSlots]);

  React.useLayoutEffect(() => {
    if (mobileMode) {
      setShowBottomScrollbar(false);
      setBottomScrollbarWidth(0);
      return;
    }

    const scroller = mainScrollRef.current;
    if (!scroller) return;

    const update = () => {
      const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 2;
      setShowBottomScrollbar(hasOverflow);
      setBottomScrollbarWidth(scroller.scrollWidth);
    };

    update();

    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => update());
    ro.observe(scroller);
    if (tableSizeRef.current) ro.observe(tableSizeRef.current);
    return () => ro.disconnect();
  }, [mobileMode, boardTab, teams, rounds, draftStyle, rankingIds, draftSlots, tableSize]);

  React.useEffect(() => {
    if (!mobileMode) return undefined;

    const scroller = mainScrollRef.current;
    if (!scroller) return undefined;

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      mainScrollTouchStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      const start = mainScrollTouchStartRef.current;
      if (!touch || !start) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      const verticalIntent = Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 4;
      if (!verticalIntent) return;

      const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop >= maxScrollTop - 1;

      if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        event.preventDefault();
      }
    };

    const clearTouchStart = () => {
      mainScrollTouchStartRef.current = null;
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    scroller.addEventListener("touchmove", onTouchMove, { passive: false });
    scroller.addEventListener("touchend", clearTouchStart);
    scroller.addEventListener("touchcancel", clearTouchStart);

    return () => {
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchmove", onTouchMove);
      scroller.removeEventListener("touchend", clearTouchStart);
      scroller.removeEventListener("touchcancel", clearTouchStart);
    };
  }, [mobileMode, boardTab]);

  const syncFromMainScroll = React.useCallback(() => {
    const scroller = mainScrollRef.current;
    const bottomBar = bottomScrollbarRef.current;
    if (!scroller || !bottomBar) return;
    if (syncingScrollRef.current === "bottom") return;
    syncingScrollRef.current = "main";
    bottomBar.scrollLeft = scroller.scrollLeft;
    requestAnimationFrame(() => {
      if (syncingScrollRef.current === "main") syncingScrollRef.current = null;
    });
  }, []);

  const syncFromBottomScroll = React.useCallback(() => {
    const scroller = mainScrollRef.current;
    const bottomBar = bottomScrollbarRef.current;
    if (!scroller || !bottomBar) return;
    if (syncingScrollRef.current === "main") return;
    syncingScrollRef.current = "bottom";
    scroller.scrollLeft = bottomBar.scrollLeft;
    requestAnimationFrame(() => {
      if (syncingScrollRef.current === "bottom") syncingScrollRef.current = null;
    });
  }, []);

  const rankingBoardSortableIds = React.useMemo(() => {
    const ids: string[] = [];

    for (let roundIndex = 0; roundIndex < rounds; roundIndex += 1) {
      const reverse = isReverseRound(roundIndex, draftStyle);

      for (let teamIndex = 0; teamIndex < teams; teamIndex += 1) {
        const pickIndex = reverse
          ? roundIndex * teams + (teams - 1 - teamIndex)
          : roundIndex * teams + teamIndex;

        const playerId = rankingIds[pickIndex];
        if (playerId) ids.push(playerId);
      }
    }

    return ids;
  }, [draftStyle, rankingIds, rounds, teams]);
  const pillBase: React.CSSProperties = {
    boxSizing: "border-box",
    padding: "7px 12px",
    cursor: "pointer",
    borderRadius: 999,
    border: "1px solid transparent",
    background: "transparent",
    color: "var(--text-0)",
    fontWeight: 750,
    letterSpacing: 0.2,
    fontSize: 13,
    lineHeight: "16px",
    transition: "transform 80ms ease, background 120ms ease, border-color 120ms ease, opacity 120ms ease, box-shadow 120ms ease",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  // Colorless translucent "selected" state (no green).
  const pillActive: React.CSSProperties = {
    border: "none",
    background: "rgba(255,255,255,0.92)",
    color: "#0f172a",
    boxShadow: "0 10px 20px rgba(0,0,0,0.22)",
    fontWeight: 850,
  };

  const mobileTabletOverscan = mobileMode && tabletMode ? 2 : 0;
  const boardContentZoom = boardContentScale * boardZoom;

  return (
    <div
      style={{
        width: mobileTabletOverscan ? `calc(100% + ${mobileTabletOverscan}px)` : "100%",
        maxWidth: mobileTabletOverscan ? `calc(100% + ${mobileTabletOverscan}px)` : "100%",

        // ✅ allow vertical expansion
        alignSelf: "stretch",
        height: mobileTabletOverscan ? `calc(100% + ${mobileTabletOverscan}px)` : "100%",
        minHeight: 0,

        boxSizing: "border-box",
        overflow: "hidden",
        zoom: mobileMode && tabletMode ? tabletScale : undefined,
        marginRight: mobileTabletOverscan ? -mobileTabletOverscan : 0,
        marginBottom: mobileTabletOverscan ? -mobileTabletOverscan : 0,
        padding: mobileMode ? `${mobileTopClearance}px 0 0` : 12,
        borderRadius: mobileMode ? 0 : 16,
        border: mobileMode ? "none" : "1px solid var(--border-0)",
        backgroundColor: "var(--panel-bg)",
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${BOARD_BG_URL})`,
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        boxShadow: mobileMode ? "none" : "var(--shadow-0)",
      }}
    >
      <style>{`
        .numStepper {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .numValue {
          min-width: 40px;
          text-align: center;
          font-weight: 800;
          font-size: 13px;
          color: var(--text-0);
          border: 1px solid var(--border-0);
          border-radius: 10px;
          padding: 4px 8px;
          background: transparent;
          user-select: none;
        }
        .numStepBtn {
          width: 26px;
          height: 26px;
          border-radius: 999px;
          border: 1px solid var(--border-0);
          background: transparent;
          color: var(--text-0);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 80ms ease, background 120ms ease, border-color 120ms ease, opacity 120ms ease;
          padding: 0;
        }
        .numStepBtn:hover {
          background: var(--panel-bg-2);
          border-color: var(--border-1);
        }
        .numStepBtn:active {
          transform: scale(0.96);
          background: var(--panel-bg-3);
        }
        .numStepBtn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .numStepBtn:focus-visible {
          outline: 2px solid var(--border-1);
          outline-offset: 2px;
        }
        .numStepIcon {
          font-size: 14px;
          line-height: 1;
          display: inline-block;
        }
        .numStepIconDown {
          transform: translateY(-0.5px);
        }
        .numStepIconUp {
          transform: translateY(-0.5px) rotate(180deg);
        }

        .tabBtn:focus-visible {
          outline: 2px solid var(--border-1);
          outline-offset: 2px;
        }
        .tabBtn:active {
          transform: scale(0.98);
        }
        .boardBottomScrollbar {
          overflow-x: auto;
          overflow-y: hidden;
          height: 18px;
          padding-top: 4px;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.48) rgba(255,255,255,0.08);
        }
        .boardBottomScrollbar::-webkit-scrollbar {
          height: 12px;
        }
        .boardBottomScrollbar::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.08);
          border-radius: 999px;
        }
        .boardBottomScrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.48);
          border-radius: 999px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .boardMainScroll::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
          background: transparent;
        }
        .boardMainScroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
      {showTabSwitcher && (
        <div
          style={{
            boxSizing: "border-box",
            display: "flex",
            justifyContent: mobileMode ? "flex-start" : "space-between",
            alignItems: "center",
            marginBottom: 8,
            gap: 12,
            zoom: !mobileMode ? uiScale : undefined,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                boxSizing: "border-box",
                display: "flex",
                gap: 4,
                alignItems: "center",
                padding: 4,
                borderRadius: 999,
                border: "1px solid var(--border-0)",
                background: "var(--panel-bg)",
              }}
            >
              {showRankTab && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Rankings Board")}
                  style={{
                    ...pillBase,
                    ...(isRank ? pillActive : {}),
                  }}
                  title="Rankings Board"
                >
                  Rankings Board
                </button>
              )}

              {showDraftTab && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Draft Board")}
                  style={{
                    ...pillBase,
                    ...(isDraft ? pillActive : {}),
                  }}
                  title="Draft Board"
                >
                  Draft Board
                </button>
              )}

              {showPlannerTab && !mobileMode && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Draft Planner")}
                  style={{
                    ...pillBase,
                    ...(isPlanner ? pillActive : {}),
                  }}
                  title="Draft Planner"
                >
                  Draft Planner
                </button>
              )}

              {showTeamsTab && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Teams")}
                  style={{
                    ...pillBase,
                    ...(isTeams ? pillActive : {}),
                  }}
                  title="Rosters"
                >
                  Rosters
                </button>
              )}
              {showCheatsheetTab && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Cheatsheet")}
                  style={{
                    ...pillBase,
                    ...(isCheat ? pillActive : {}),
                  }}
                  title="Cheatsheet"
                >
                  Cheatsheet
                </button>
              )}
              {showCompareTab && (
                <button
                  className="tabBtn"
                  onClick={() => setBoardTab("Compare Rankings")}
                  style={{
                    ...pillBase,
                    ...(isCompare ? pillActive : {}),
                  }}
                  title="Compare Rankings"
                >
                  Compare Rankings
                </button>
              )}
            </div>
          </div>

          {!mobileMode && (
            <div ref={desktopSettingsRef} style={{ position: "relative", display: "flex", alignItems: "center", gap: 10 }}>
              {onRefreshDraftSync ? (
                <button
                  type="button"
                  onClick={onRefreshDraftSync}
                  aria-label="Refresh synced league"
                  title="Refresh synced league"
                  disabled={isRefreshingDraftSync}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 0,
                    border: "none",
                    background: "transparent",
                    color: "var(--text-0)",
                    cursor: isRefreshingDraftSync ? "progress" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    opacity: isRefreshingDraftSync ? 0.7 : 0.92,
                  }}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    style={{
                      transform: isRefreshingDraftSync ? "rotate(180deg)" : "none",
                      transition: isRefreshingDraftSync ? "transform 900ms linear" : "transform 180ms ease",
                    }}
                  >
                    <path
                      d="M20 5v5h-5"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 10a8 8 0 1 0 2 5.3"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => setDesktopSettingsOpen((open) => !open)}
                aria-label="Open draft settings"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 0,
                  border: "none",
                  background: "transparent",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  padding: 0,
                  opacity: desktopSettingsOpen ? 1 : 0.92,
                }}
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.757.426 1.757 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.757-2.924 1.757-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.757-.426-1.757-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065Z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="3.25" stroke="currentColor" strokeWidth="1.75" />
                </svg>
              </button>

              {desktopSettingsOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 48,
                    width: 360,
                    maxWidth: "min(360px, calc(100vw - 32px))",
                    padding: 14,
                    borderRadius: 18,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(14,18,28,0.98)",
                    boxShadow: "0 24px 54px rgba(0,0,0,0.42)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    zIndex: 120,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                      Draft Settings
                    </div>
                    <button
                      type="button"
                      onClick={() => setDesktopSettingsOpen(false)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 8, alignItems: "center" }}>
                    <input
                      value={newPlayerName}
                      onChange={(e) => setNewPlayerName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitAddPlayer();
                      }}
                      placeholder="Add player name"
                      aria-label="Add player name"
                      style={{
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        padding: "8px 10px",
                        outline: "none",
                        minWidth: 0,
                      }}
                    />
                    <select
                      value={newPlayerPos}
                      onChange={(e) => setNewPlayerPos(e.target.value as Position)}
                      aria-label="Add player position"
                      style={{
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        padding: "8px 10px",
                        outline: "none",
                      }}
                    >
                      {(["QB", "RB", "WR", "TE", "K", "DST"] as Position[]).map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={submitAddPlayer}
                      disabled={!canAddPlayer}
                      aria-label="Add player"
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: canAddPlayer ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                        color: "var(--text-0)",
                        fontWeight: 800,
                        fontSize: 12,
                        cursor: canAddPlayer ? "pointer" : "not-allowed",
                      }}
                    >
                      Add
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.04)",
                        padding: "8px 10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Teams</span>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <button type="button" onClick={() => setTeams((n) => Math.max(2, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>−</button>
                        <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{teams}</span>
                        <button type="button" onClick={() => setTeams((n) => Math.min(20, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>+</button>
                      </div>
                    </div>

                    <div
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.04)",
                        padding: "8px 10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Rounds</span>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <button type="button" onClick={() => setRounds((n) => Math.max(1, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>−</button>
                        <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{rounds}</span>
                        <button type="button" onClick={() => setRounds((n) => Math.min(40, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>+</button>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)" }}>Draft Style</span>
                    <select
                      value={draftStyle}
                      onChange={(e) => setDraftStyle(e.target.value as DraftStyle)}
                      style={{
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        padding: "8px 10px",
                        outline: "none",
                      }}
                    >
                      <option value="Snake Draft">Snake Draft</option>
                      <option value="Regular Draft">Regular Draft</option>
                      <option value="Third Round Reversal">Third Round Reversal</option>
                    </select>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setDesktopSettingsOpen(false);
                        onOpenDraftSync?.();
                      }}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.08)",
                        color: "var(--text-0)",
                        cursor: "pointer",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      Sync Draft
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRestartDraftOpen(true)}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(239,68,68,0.24)",
                        background: "rgba(239,68,68,0.12)",
                        color: "var(--text-0)",
                        cursor: "pointer",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      Restart Draft
                    </button>
                  </div>

                  {confirmRestartDraftOpen ? (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 121,
                        borderRadius: 16,
                        background: "rgba(6,8,16,0.72)",
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                      }}
                      onMouseDown={() => setConfirmRestartDraftOpen(false)}
                    >
                      <div
                        style={{
                          width: "100%",
                          maxWidth: 290,
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(12,18,34,0.98)",
                          boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
                          padding: 14,
                          display: "grid",
                          gap: 12,
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <div style={{ display: "grid", gap: 6 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Restart Draft?</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                            This will clear every drafted player from the current board.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => setConfirmRestartDraftOpen(false)}
                            style={{
                              padding: "7px 11px",
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.12)",
                              background: "rgba(255,255,255,0.05)",
                              color: "var(--text-0)",
                              fontSize: 11,
                              fontWeight: 800,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              clearAllDrafted();
                              setConfirmRestartDraftOpen(false);
                              setDesktopSettingsOpen(false);
                            }}
                            style={{
                              padding: "7px 11px",
                              borderRadius: 999,
                              border: "1px solid rgba(239,68,68,0.24)",
                              background: "rgba(239,68,68,0.12)",
                              color: "var(--text-0)",
                              fontSize: 11,
                              fontWeight: 800,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Restart Draft
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div
        ref={mainScrollRef}
        className="boardMainScroll"
        onScroll={syncFromMainScroll}
        style={{
          overflow: "auto",
          height: "100%",
          overscrollBehavior: mobileMode ? "none" : undefined,
          WebkitOverflowScrolling: mobileMode ? "touch" : undefined,
          touchAction: mobileMode ? "pan-x pan-y" : undefined,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {false && showTabSwitcher && (
          <div
            style={{
              boxSizing: "border-box",
              display: "flex",
              justifyContent: mobileMode ? "flex-start" : "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 12,
              zoom: !mobileMode ? uiScale : undefined,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* ADP/Draft segmented control */}
              <div
                style={{
                  boxSizing: "border-box",
                  display: "flex",
                  gap: 4,
                  alignItems: "center",
                  padding: 4,
                  borderRadius: 999,
                  border: "1px solid var(--border-0)",
                  background: "var(--panel-bg)",
                }}
              >
                {showRankTab && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Rankings Board")}
                    style={{
                      ...pillBase,
                      ...(isRank ? pillActive : {}),
                    }}
                    title="Rankings Board"
                  >
                    Rankings Board
                  </button>
                )}

                {showDraftTab && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Draft Board")}
                    style={{
                      ...pillBase,
                      ...(isDraft ? pillActive : {}),
                    }}
                    title="Draft Board"
                  >
                    Draft Board
                  </button>
                )}

                {showPlannerTab && !mobileMode && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Draft Planner")}
                    style={{
                      ...pillBase,
                      ...(isPlanner ? pillActive : {}),
                    }}
                    title="Draft Planner"
                  >
                    Draft Planner
                  </button>
                )}

                {showTeamsTab && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Teams")}
                    style={{
                      ...pillBase,
                      ...(isTeams ? pillActive : {}),
                    }}
                    title="Rosters"
                  >
                    Rosters
                  </button>
                )}
                {showCheatsheetTab && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Cheatsheet")}
                    style={{
                      ...pillBase,
                      ...(isCheat ? pillActive : {}),
                    }}
                    title="Cheatsheet"
                  >
                    Cheatsheet
                  </button>
                )}
                {showCompareTab && (
                  <button
                    className="tabBtn"
                    onClick={() => setBoardTab("Compare Rankings")}
                    style={{
                      ...pillBase,
                      ...(isCompare ? pillActive : {}),
                    }}
                    title="Compare Rankings"
                  >
                    Compare Rankings
                  </button>
                )}
                </div>
            </div>

            {!mobileMode && (
              <div ref={desktopSettingsRef} style={{ position: "relative", display: "flex", alignItems: "center", gap: 10 }}>
                {onRefreshDraftSync ? (
                  <button
                    type="button"
                    onClick={onRefreshDraftSync}
                    aria-label="Refresh synced league"
                    title="Refresh synced league"
                    disabled={isRefreshingDraftSync}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 0,
                      border: "none",
                      background: "transparent",
                      color: "var(--text-0)",
                      cursor: isRefreshingDraftSync ? "progress" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      opacity: isRefreshingDraftSync ? 0.7 : 0.92,
                    }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{
                        transform: isRefreshingDraftSync ? "rotate(180deg)" : "none",
                        transition: isRefreshingDraftSync ? "transform 900ms linear" : "transform 180ms ease",
                      }}
                    >
                      <path
                        d="M20 5v5h-5"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 10a8 8 0 1 0 2 5.3"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => setDesktopSettingsOpen((open) => !open)}
                  aria-label="Open draft settings"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 0,
                    border: "none",
                    background: "transparent",
                    color: "var(--text-0)",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    padding: 0,
                    opacity: desktopSettingsOpen ? 1 : 0.92,
                  }}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.757.426 1.757 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.757-2.924 1.757-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.757-.426-1.757-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065Z"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3.25" stroke="currentColor" strokeWidth="1.75" />
                  </svg>
                </button>

                {desktopSettingsOpen && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 48,
                      width: 360,
                      maxWidth: "min(360px, calc(100vw - 32px))",
                      padding: 14,
                      borderRadius: 18,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(14,18,28,0.98)",
                      boxShadow: "0 24px 54px rgba(0,0,0,0.42)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      zIndex: 120,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                        Draft Settings
                      </div>
                      <button
                        type="button"
                        onClick={() => setDesktopSettingsOpen(false)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--text-0)",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 8, alignItems: "center" }}>
                      <input
                        value={newPlayerName}
                        onChange={(e) => setNewPlayerName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") submitAddPlayer();
                        }}
                        placeholder="Add player name"
                        aria-label="Add player name"
                        style={{
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--text-0)",
                          padding: "8px 10px",
                          outline: "none",
                          minWidth: 0,
                        }}
                      />
                      <select
                        value={newPlayerPos}
                        onChange={(e) => setNewPlayerPos(e.target.value as Position)}
                        aria-label="Add player position"
                        style={{
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--text-0)",
                          padding: "8px 10px",
                          outline: "none",
                        }}
                      >
                        {(["QB", "RB", "WR", "TE", "K", "DST"] as Position[]).map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={submitAddPlayer}
                        disabled={!canAddPlayer}
                        aria-label="Add player"
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: canAddPlayer ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                          color: "var(--text-0)",
                          fontWeight: 800,
                          fontSize: 12,
                          cursor: canAddPlayer ? "pointer" : "not-allowed",
                        }}
                      >
                        Add
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div
                        style={{
                          borderRadius: 12,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.04)",
                          padding: "8px 10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Teams</span>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <button type="button" onClick={() => setTeams((n) => Math.max(2, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>−</button>
                          <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{teams}</span>
                          <button type="button" onClick={() => setTeams((n) => Math.min(20, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>+</button>
                        </div>
                      </div>

                      <div
                        style={{
                          borderRadius: 12,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.04)",
                          padding: "8px 10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Rounds</span>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <button type="button" onClick={() => setRounds((n) => Math.max(1, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>−</button>
                          <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{rounds}</span>
                          <button type="button" onClick={() => setRounds((n) => Math.min(40, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: 28, height: 28 }}>+</button>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)" }}>Draft Style</span>
                      <select
                        value={draftStyle}
                        onChange={(e) => setDraftStyle(e.target.value as DraftStyle)}
                        style={{
                          borderRadius: 10,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--text-0)",
                          padding: "8px 10px",
                          outline: "none",
                        }}
                      >
                        <option value="Snake Draft">Snake Draft</option>
                        <option value="Regular Draft">Regular Draft</option>
                        <option value="Third Round Reversal">Third Round Reversal</option>
                      </select>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setDesktopSettingsOpen(false);
                          onOpenDraftSync?.();
                        }}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.08)",
                          color: "var(--text-0)",
                          cursor: "pointer",
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        Sync Draft
                      </button>
                      <button
                        type="button"
                        onClick={clearAllDrafted}
                        style={{
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "var(--text-0)",
                          cursor: "pointer",
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        Undraft All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div ref={boardViewportRef} style={{ width: "100%", minWidth: 0 }}>
        {boardTab === "Rankings Board" ? (
          <div
            ref={tableSizeRef}
            style={{
              width: "fit-content",
              zoom: boardContentZoom,
            }}
          >
            {mobileMode ? (
              <>
                {Array.from({ length: rounds }).map((_, roundIndex) => {
                  const reverse = isReverseRound(roundIndex, draftStyle);
                  const roundNumber = roundIndex + 1;

                  return (
                    <div key={roundIndex} style={{ marginBottom: tabletMode ? -8 : -8 }}>
                      <div style={{ display: "flex" }}>
                        {Array.from({ length: teams }).map((_, teamIndex) => {
                          const pickIndex = reverse
                            ? roundIndex * teams + (teams - 1 - teamIndex)
                            : roundIndex * teams + teamIndex;

                          const playerId = rankingIds[pickIndex] ?? null;
                          const player = playerId ? playersById[playerId] : undefined;
                          const pickInRound = reverse ? teams - teamIndex : teamIndex + 1;
                          const baseLabel = `${roundNumber}.${String(pickInRound).padStart(2, "0")}`;
                          const directionArrow = reverse ? "←" : "→";

                          return (
                            <MobileRankingsBoardCard
                              key={playerId ?? `rankingslot:${pickIndex}`}
                              pickLabel={baseLabel}
                              directionArrow={directionArrow}
                              player={player}
                              drafted={playerId ? draftedIds.has(playerId) : false}
                              bg={player ? posColor(player.position) : "var(--surface-0)"}
                              marginRight={boardCellMarginRight}
                              tabletMode={tabletMode}
                              onOpenProfile={onOpenPlayerProfile}
                              shouldSuppressTap={shouldSuppressMobileBoardTap}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <DndContext sensors={allowRankingsReorder ? sensors : undefined} onDragEnd={allowRankingsReorder ? onBoardDragEnd : undefined}>
                <SortableContext items={rankingBoardSortableIds} strategy={rectSortingStrategy}>
                  {Array.from({ length: rounds }).map((_, roundIndex) => {
                    const reverse = isReverseRound(roundIndex, draftStyle);
                    const roundNumber = roundIndex + 1;

                    return (
                      <div key={roundIndex} style={{ marginBottom: 2 }}>
                        <div style={{ display: "flex" }}>
                          {Array.from({ length: teams }).map((_, teamIndex) => {
                            const pickIndex = reverse
                              ? roundIndex * teams + (teams - 1 - teamIndex)
                              : roundIndex * teams + teamIndex;

                            const playerId = rankingIds[pickIndex] ?? null;
                            const player = playerId ? playersById[playerId] : undefined;

                            const pickInRound = reverse ? teams - teamIndex : teamIndex + 1;
                            const baseLabel = `${roundNumber}.${String(pickInRound).padStart(2, "0")}`;
                            const label = reverse ? `← ${baseLabel}` : `${baseLabel} →`;

                            if (!playerId || !player) {
                              return (
                                <BoardCell
                                  key={`rankingslot:${pickIndex}`}
                                  id={`rankingslot:${pickIndex}`}
                                  drafted={false}
                                  dimWhenDrafted={false}
                                  showDraftedBanner={false}
                                  onToggleDrafted={() => {}}
                                  bg={"var(--surface-0)"}
                                  sortable={false}
                                  width={boardCellWidth}
                                  minWidth={boardCellMinWidth}
                                  borderRadius={boardCellRadius}
                                  padding={boardCellPadding}
                                  marginRight={boardCellMarginRight}
                                  clickable
                                >
                                  <CellContent
                                    label={label}
                                    name={"—"}
                                    position={" "}
                                    team={undefined}
                                    imageUrl={undefined}
                                    showDash={false}
                                    showImage
                                  />
                                </BoardCell>
                              );
                            }

                            return (
                              <BoardCell
                                key={playerId}
                                id={playerId}
                                drafted={draftedIds.has(playerId)}
                                dimWhenDrafted={true}
                                showDraftedBanner={true}
                                onToggleDrafted={onToggleDrafted}
                                bg={posColor(player.position)}
                                sortable={allowRankingsReorder}
                                width={boardCellWidth}
                                minWidth={boardCellMinWidth}
                                borderRadius={boardCellRadius}
                                padding={boardCellPadding}
                                marginRight={boardCellMarginRight}
                                clickable
                              >
                                <CellContent
                                  label={label}
                                  name={player.name}
                                  position={player.position}
                                  team={player.team}
                                  imageUrl={player.imageUrl}
                                  showDash
                                  showImage
                                />
                              </BoardCell>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}
          </div>
        ) : boardTab === "Draft Planner" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              paddingTop: 4,
              zoom: boardZoom,
            }}
          >
            <DndContext
              sensors={sensors}
              onDragStart={onPlannerDragStart}
              onDragOver={onPlannerDragOver}
              onDragEnd={onPlannerDragEnd}
              onDragCancel={() => setActivePlannerPlayerId(null)}
            >
              {plannerRows.map((playerIds, rowIndex) => (
                <PlannerRoundLane
                  key={`planner-round-${rowIndex + 1}`}
                  round={rowIndex + 1}
                  playerIds={playerIds}
                  playersById={playersById}
                  draftedIds={draftedIds}
                  posColor={posColor}
                  activePlannerPlayerId={activePlannerPlayerId}
                  isCurrentRound={currentDraftRound === rowIndex + 1}
                  onToggleFavorite={onToggleFavorite}
                  onOpenAddPlayer={openPlannerAssignModal}
                  mobileMode={mobileMode}
                  tabletMode={tabletMode}
                />
              ))}
              <DragOverlay>
                {activePlannerPlayerId && playersById[activePlannerPlayerId] ? (
                  <PlannerFavoriteCard
                    player={playersById[activePlannerPlayerId]!}
                    drafted={draftedIds.has(activePlannerPlayerId)}
                    posColor={posColor}
                    sortableEnabled={false}
                    onToggleFavorite={onToggleFavorite}
                    mobileMode={mobileMode}
                    tabletMode={tabletMode}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        ) : boardTab === "Compare Rankings" ? (
          <div
            style={{
              minWidth: mobileMode ? 0 : tableSize?.width,
              minHeight: mobileMode ? undefined : tableSize?.height,
              width: mobileMode ? "100%" : undefined,
              display: "flex",
              flexDirection: "column",
              gap: 0,
              position: "relative",
              zoom: boardZoom,
            }}
          >
            <CompareRankingsView
              columns={compareColumns}
              playersById={playersById}
              onImportRankings={onImportCompareRankings ?? (() => {})}
              onReorderColumns={onReorderCompareColumns ?? (() => {})}
              onMoveMyRankings={onMoveMyCompareRankings ?? (() => {})}
              onRemoveColumn={onRemoveCompareColumn ?? (() => {})}
            />
          </div>
        ) : boardTab === "Cheatsheet" ? (
          <div
            style={{
              // Reserve the same space as the main board so tab switching doesn't "shrink" the container.
              minWidth: mobileMode ? 0 : tableSize?.width,
              minHeight: tableSize?.height,
              width: mobileMode ? "100%" : undefined,

              // Center the cheatsheet columns within the reserved space.
              display: "flex",
              justifyContent: mobileMode ? "flex-start" : "center",
              flexDirection: "column",
              gap: 0,
              position: "relative",
              zoom: boardZoom,
            }}
          >
            <Cheatsheet
              favoriteIds={favoriteIds}
              rankingIds={cheatsheetRankingIds}
              rankingsRankingIds={cheatsheetRankingIds}
              playersById={playersById}
              tiersByPos={cheatsheetTiersByPos}
              onUpdateTiersByPos={cheatsheetOnUpdateTiersByPos}
              allowTierEditing={!!cheatsheetOnUpdateTiersByPos}
              draftedIds={draftedIds}
              onToggleDrafted={onToggleDrafted}
              allowDraftToggle={!mobileMode}
              onOpenProfile={mobileMode ? onOpenPlayerProfile : undefined}
              posColor={posColor}
              fitToViewport={mobileMode}
              visiblePositions={mobileMode ? cheatsheetVisiblePositions : undefined}
            />
            {mobileMode && mobileCheatsheetViewOpen ? (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 2200,
                  background: "rgba(4,7,18,0.46)",
                  backdropFilter: "blur(3px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: tabletMode ? 20 : 14,
                }}
                onPointerDown={() => setMobileCheatsheetViewOpen(false)}
              >
                <div
                  style={{
                    width: tabletMode ? "min(360px, calc(100vw - 40px))" : "min(320px, calc(100vw - 28px))",
                    padding: tabletMode ? 14 : 12,
                    borderRadius: 16,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(18,18,22,0.98)",
                    boxShadow: "0 24px 50px rgba(0,0,0,0.42)",
                    display: "grid",
                    gap: 10,
                  }}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                      Cheatsheet View
                    </div>
                    <button
                      type="button"
                      onClick={() => setMobileCheatsheetViewOpen(false)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                    Position Columns
                  </div>
                  <div style={{ display: "grid", gap: tabletMode ? 8 : 6 }}>
                    {cheatsheetPresentPositions.map((pos) => {
                      const active = mobileCheatsheetVisiblePositions[pos];
                      const visibleCount = cheatsheetPresentPositions.filter((candidate) => mobileCheatsheetVisiblePositions[candidate]).length;
                      const disableHide = active && visibleCount <= 1;
                      return (
                        <label
                          key={pos}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: tabletMode ? "10px 12px" : "9px 10px",
                            borderRadius: 12,
                            border: "1px solid rgba(255,255,255,0.12)",
                            background: active ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            color: "var(--text-0)",
                            fontSize: tabletMode ? 12 : 11,
                            fontWeight: 800,
                            cursor: disableHide ? "default" : "pointer",
                            opacity: disableHide ? 0.55 : 1,
                          }}
                        >
                          <span>{pos}</span>
                          <input
                            className="mobileOverlayCheckbox"
                            type="checkbox"
                            checked={active}
                            disabled={disableHide}
                            onChange={() =>
                              setMobileCheatsheetVisiblePositions((prev) => ({
                                ...prev,
                                [pos]: !prev[pos],
                              }))
                            }
                            style={{
                              width: tabletMode ? 18 : 16,
                              height: tabletMode ? 18 : 16,
                              margin: 0,
                              cursor: disableHide ? "not-allowed" : "pointer",
                              flex: "0 0 auto",
                            }}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : boardTab === "Teams" ? (
          <div
            ref={tableSizeRef}
            style={{
              width: "fit-content",
              minWidth: mobileMode ? undefined : tableSize?.width,
              minHeight: tableSize?.height,
              zoom: boardContentZoom,
            }}
          >
            <TeamsBoard
              teams={teams}
              rounds={rounds}
              draftStyle={draftStyle}
              draftSlots={draftSlots}
              draftSlotTeamIndexes={draftSlotTeamIndexes}
              teamNames={teamNames}
              playersById={playersById}
              posColor={posColor}
              starterConfig={rosterStarterConfig}
              setStarterConfig={setRosterStarterConfig}
              teamOverrides={rosterTeamOverrides}
              setTeamOverrides={setRosterTeamOverrides}
              fitToViewport={mobileMode}
              scaleToViewport={false}
              compactLayout={tabletMode ? "tablet" : "phone"}
              mobileViewOpen={mobileTeamsViewOpen}
              setMobileViewOpen={setMobileTeamsViewOpen}
            />
          </div>
        ) : (
          <div
            ref={tableSizeRef}
            style={{
              width: "fit-content",
              zoom: boardContentZoom,
            }}
          >
            <DndContext
              sensors={allowDraftBoardReorder ? sensors : undefined}
              onDragEnd={allowDraftBoardReorder ? onDraftBoardDragEnd : undefined}
            >
              <div style={{ display: "flex" }}>
                {Array.from({ length: teams }).map((_, teamIndex) => (
                  <div
                    key={teamIndex}
                    onPointerDownCapture={(event) => {
                      event.stopPropagation();
                    }}
                    onPointerUp={(event) => {
                      event.stopPropagation();
                      focusTeamNameInput(teamIndex);
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      focusTeamNameInput(teamIndex);
                    }}
                    style={{
                      boxSizing: "border-box",
                      width: mobileMode ? (tabletMode ? 91 : 72) : boardCellWidth,
                      minWidth: mobileMode ? (tabletMode ? 91 : 72) : boardCellMinWidth,
                      marginRight: boardCellMarginRight,
                      borderRadius: mobileMode ? 7 : boardCellRadius,
                      border: "1px solid rgba(255,255,255,0.10)",
                      outline: "1px solid rgba(0,0,0,0.18)",
                      background: "var(--panel-bg)",
                      padding: mobileMode ? (tabletMode ? 5 : 4) : boardCellPadding,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "text",
                    }}
                  >
                    <input
                      ref={(input) => {
                        teamNameInputRefs.current[teamIndex] = input;
                      }}
                      value={teamNames[teamIndex] ?? ""}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTeamNames((prev) => {
                          const next = [...prev];
                          next[teamIndex] = value;
                          return next;
                        });
                      }}
                      style={{
                        boxSizing: "border-box",
                        width: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        color: "var(--text-0)",
                        fontWeight: 800,
                        fontSize: mobileMode ? 12 : 13,
                        textAlign: "center",
                        padding: 2,
                      }}
                      placeholder={`Team ${teamIndex + 1}`}
                    />
                  </div>
                ))}
              </div>

              <SortableContext
                items={draftSlots.map((_, i) => `draftslot:${i}`)}
                strategy={horizontalListSortingStrategy}
              >
                <>
                  {Array.from({ length: rounds }).map((_, roundIndex) => {
                    const reverse = isReverseRound(roundIndex, draftStyle);
                    const roundNumber = roundIndex + 1;

                    return (
                      <div key={roundIndex} style={{ marginBottom: mobileMode ? -8 : 2 }}>
                        <div style={{ display: "flex" }}>
                          {Array.from({ length: teams }).map((_, teamIndex) => {
                          const pickIndex = reverse
                            ? roundIndex * teams + (teams - 1 - teamIndex)
                            : roundIndex * teams + teamIndex;

                          const pickInRound = reverse ? teams - teamIndex : teamIndex + 1;
                          const baseLabel = `${roundNumber}.${String(pickInRound).padStart(2, "0")}`;
                          const label = reverse ? `← ${baseLabel}` : `${baseLabel} →`;
                          const directionArrow = reverse ? "←" : "→";
                          const syncedOwnerIndex = draftSlotTeamIndexes?.[pickIndex];
                          const ownerTeamIndex =
                            typeof syncedOwnerIndex === "number" &&
                            Number.isFinite(syncedOwnerIndex) &&
                            syncedOwnerIndex >= 0 &&
                            syncedOwnerIndex < teams
                              ? Math.round(syncedOwnerIndex)
                              : null;
                          const ownerLabel =
                            ownerTeamIndex != null
                              ? teamNames[ownerTeamIndex]?.trim() || `Team ${ownerTeamIndex + 1}`
                              : undefined;
                          const ownerIsTraded = ownerTeamIndex != null && ownerTeamIndex !== teamIndex;

                          const playerId = draftSlots[pickIndex];
                          if (!playerId) {
                            if (mobileMode) {
                              return (
                                <MobileRankingsBoardCard
                                  key={`draftslot:${pickIndex}`}
                                  pickLabel={baseLabel}
                                  directionArrow={directionArrow}
                                  player={undefined}
                                  bg={"var(--surface-0)"}
                                  marginRight={boardCellMarginRight}
                                  tabletMode={tabletMode}
                                  dimDrafted={false}
                                  ownerLabel={ownerLabel}
                                  ownerIsTraded={ownerIsTraded}
                                  shouldSuppressTap={shouldSuppressMobileBoardTap}
                                  onOpenAssign={() => {
                                    setMobileDraftAssignPrompt({ slotIndex: pickIndex, label: baseLabel });
                                  }}
                                />
                              );
                            }
                            return (
                              <BoardCell
                                key={`draftslot:${pickIndex}`}
                                id={`draftslot:${pickIndex}`}
                                drafted={false}
                                dimWhenDrafted={false}
                                showDraftedBanner={false}
                                onToggleDrafted={() => {}}
                                onClick={mobileMode ? () => {} : (e) => {
                                  const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                  setDraftRemoveMenu(null);
                                  setDraftAssignMenu({
                                    slotIndex: pickIndex,
                                    x: rect.left + rect.width / 2,
                                    y: rect.top + rect.height / 2,
                                    label,
                                  });
                                }}
                                bg={"var(--surface-0)"}
                                sortable={allowDraftBoardReorder}
                                width={boardCellWidth}
                                minWidth={boardCellMinWidth}
                                minHeight={draftBoardCellMinHeight}
                                borderRadius={boardCellRadius}
                                padding={boardCellPadding}
                                marginRight={boardCellMarginRight}
                                clickable={!mobileMode}
                              >
                                <CellContent
                                  label={label}
                                  name={"—"}
                                  position={" "}
                                  team={undefined}
                                  imageUrl={undefined}
                                  showDash={false}
                                  showImage={showBoardCellImages}
                                  compact={compactMobileMode}
                                  clampNameLines={2}
                                  ownerLabel={ownerLabel}
                                  ownerIsTraded={ownerIsTraded}
                                />
                              </BoardCell>
                            );
                          }

                          const player = playersById[playerId];
                          if (!player) {
                            if (mobileMode) {
                              return (
                                <MobileRankingsBoardCard
                                  key={`draftslot:${pickIndex}`}
                                  pickLabel={baseLabel}
                                  directionArrow={directionArrow}
                                  player={undefined}
                                  drafted={draftedIds.has(playerId)}
                                  bg={"var(--surface-0)"}
                                  marginRight={boardCellMarginRight}
                                  tabletMode={tabletMode}
                                  dimDrafted={false}
                                  ownerLabel={ownerLabel}
                                  ownerIsTraded={ownerIsTraded}
                                />
                              );
                            }
                            return (
                              <BoardCell
                                key={`draftslot:${pickIndex}`}
                                id={`draftslot:${pickIndex}`}
                                drafted={draftedIds.has(playerId)}
                                dimWhenDrafted={false}
                              showDraftedBanner={false}
                                onToggleDrafted={onToggleDrafted}
                                bg={"var(--surface-0)"}
                                sortable={allowDraftBoardReorder}
                                width={boardCellWidth}
                                minWidth={boardCellMinWidth}
                                minHeight={draftBoardCellMinHeight}
                                borderRadius={boardCellRadius}
                                padding={boardCellPadding}
                                marginRight={boardCellMarginRight}
                                clickable={!mobileMode}
                              >
                              <CellContent
                                label={label}
                                name={"—"}
                                position={" "}
                                team={undefined}
                                imageUrl={undefined}
                                showDash={false}
                                showImage={showBoardCellImages}
                                compact={compactMobileMode}
                                clampNameLines={2}
                                ownerLabel={ownerLabel}
                                ownerIsTraded={ownerIsTraded}
                              />
                              </BoardCell>
                            );
                          }

                          if (mobileMode) {
                            return (
                              <MobileRankingsBoardCard
                                key={`draftslot:${pickIndex}`}
                                pickLabel={baseLabel}
                                directionArrow={directionArrow}
                                player={player}
                                drafted={draftedIds.has(playerId)}
                                bg={posColor(player.position)}
                                marginRight={boardCellMarginRight}
                                tabletMode={tabletMode}
                                dimDrafted={false}
                                ownerLabel={ownerLabel}
                                ownerIsTraded={ownerIsTraded}
                                onOpenProfile={onOpenPlayerProfile}
                                shouldSuppressTap={shouldSuppressMobileBoardTap}
                              />
                            );
                          }

                          return (
                            <BoardCell
                              key={`draftslot:${pickIndex}`}
                              id={`draftslot:${pickIndex}`}
                              drafted={draftedIds.has(playerId)}
                              dimWhenDrafted={false}
                              showDraftedBanner={false}
                              onToggleDrafted={mobileMode ? () => {} : onToggleDrafted}
                              onClick={mobileMode ? () => {} : (e) => {
                                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                setDraftAssignMenu(null);
                                setDraftRemoveMenu({
                                  slotIndex: pickIndex,
                                  x: rect.left + rect.width / 2,
                                  y: rect.top + rect.height / 2,
                                  label,
                                  playerId,
                                  playerName: player.name,
                                });
                              }}
                              bg={posColor(player.position)}
                              sortable={allowDraftBoardReorder}
                              width={boardCellWidth}
                              minWidth={boardCellMinWidth}
                              minHeight={draftBoardCellMinHeight}
                              borderRadius={boardCellRadius}
                              padding={boardCellPadding}
                              marginRight={boardCellMarginRight}
                              clickable={!mobileMode}
                            >
                              <CellContent
                                label={label}
                                name={player.name}
                                position={player.position}
                                team={player.team}
                                imageUrl={player.imageUrl}
                                showDash
                                showImage={showBoardCellImages}
                                compact={compactMobileMode}
                                clampNameLines={2}
                                ownerLabel={ownerLabel}
                                ownerIsTraded={ownerIsTraded}
                              />
                            </BoardCell>
                          );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              </SortableContext>
            </DndContext>
          </div>
        )}
        </div>
      {!mobileMode && showBottomScrollbar ? (
        <div
          ref={bottomScrollbarRef}
          className="boardBottomScrollbar"
          onScroll={syncFromBottomScroll}
        >
          <div style={{ width: bottomScrollbarWidth, height: 1 }} />
        </div>
      ) : null}

{isDraft && mobileMode && mobileDraftAssignPrompt ? (
  <div
    onClick={() => setMobileDraftAssignPrompt(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2100,
      background: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(88vw, 320px)",
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(18,18,22,0.98)",
        boxShadow: "0 22px 50px rgba(0,0,0,0.45)",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,255,255,0.72)", letterSpacing: 0.4, textTransform: "uppercase" }}>
        Draft Position
      </div>
      <button
        type="button"
        onClick={() => {
          setDraftAssignModal({ slotIndex: mobileDraftAssignPrompt.slotIndex, label: mobileDraftAssignPrompt.label });
          setDraftAssignQuery("");
          setMobileDraftAssignPosition("ALL");
          setMobileDraftAssignPrompt(null);
        }}
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.08)",
          color: "var(--text-0)",
          fontWeight: 900,
          fontSize: 15,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {`Assign ${mobileDraftAssignPrompt.label}`}
      </button>
    </div>
  </div>
) : null}

{isDraft && mobileMode && draftAssignModal ? (
  <div
    onClick={() => setDraftAssignModal(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2200,
      background: "rgba(0,0,0,0.55)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        height: "100%",
        background: "rgba(18,18,22,0.98)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: tabletMode ? "14px 14px 10px" : "10px 10px 8px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontSize: tabletMode ? 18 : 16, fontWeight: 900, color: "#fff" }}>
            {`Assign ${draftAssignModal.label}`}
          </div>
          <button
            type="button"
            onClick={() => setDraftAssignModal(null)}
            style={{
              width: tabletMode ? 38 : 34,
              height: tabletMode ? 38 : 34,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "var(--text-0)",
              fontSize: tabletMode ? 20 : 18,
              lineHeight: 1,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <input
          ref={draftAssignInputRef}
          autoFocus
          value={draftAssignQuery}
          onChange={(e) => setDraftAssignQuery(e.target.value)}
          placeholder="Search players"
          style={{
            marginTop: 10,
            width: "100%",
            padding: tabletMode ? "10px 12px" : "8px 10px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--text-0)",
            outline: "none",
            fontWeight: 750,
            fontSize: tabletMode ? 14 : 12,
          }}
        />
        <div
          style={{
            display: "flex",
            gap: tabletMode ? 8 : 6,
            overflowX: "auto",
            marginTop: 10,
            paddingBottom: 2,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {mobileDraftAssignPositionTabs.map((tab) => {
            const active = mobileDraftAssignPosition === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setMobileDraftAssignPosition(tab)}
                style={{
                  flex: "0 0 auto",
                  padding: tabletMode ? "8px 13px" : "6px 10px",
                  borderRadius: 999,
                  border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
                  background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.05)",
                  color: active ? "#0f172a" : "var(--text-0)",
                  fontSize: tabletMode ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {tab === "ALL" ? "All" : tab}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {mobileDraftAssignCandidates.length === 0 ? (
          <div style={{ padding: 18, opacity: 0.8, fontWeight: 750, color: "var(--text-0)" }}>
            No matching available players.
          </div>
        ) : (
          mobileDraftAssignCandidates.map((id, index) => {
            const p = playersById[id];
            if (!p) return null;
            const baseRowBg = index % 2 === 0 ? "rgba(0,0,0,0.78)" : "rgba(35, 35, 35, 0.86)";
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  onAssignPlayerToDraftSlot(draftAssignModal.slotIndex, id);
                  if (!draftedIds.has(id)) onToggleDrafted(id);
                  setDraftAssignModal(null);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  background: baseRowBg,
                  color: "var(--text-0)",
                  display: "grid",
                  gridTemplateColumns: tabletMode ? "42px minmax(0, 1fr)" : "34px minmax(0, 1fr)",
                  gap: tabletMode ? 12 : 10,
                  alignItems: "center",
                  padding: tabletMode ? "10px 14px" : "8px 10px",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: tabletMode ? 14 : 12,
                    fontWeight: 900,
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {rankingIds.indexOf(id) + 1}
                </div>
                <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: tabletMode ? 12 : 9 }}>
                  <img
                    src={p.imageUrl || "/headshot-placeholder.svg"}
                    alt={p.name}
                    style={{
                      width: tabletMode ? 36 : 30,
                      height: tabletMode ? 36 : 30,
                      borderRadius: 999,
                      objectFit: "cover",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.28)",
                      flex: "0 0 auto",
                    }}
                    onError={(event) => {
                      const img = event.currentTarget;
                      if (!img.src.includes("/headshot-placeholder.svg")) img.src = "/headshot-placeholder.svg";
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: "var(--text-0)",
                        fontSize: tabletMode ? 18 : 14,
                        fontWeight: 900,
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </div>
                    <div style={{ marginTop: tabletMode ? 3 : 2, display: "flex", alignItems: "center", gap: tabletMode ? 8 : 7, color: "rgba(255,255,255,0.68)", fontSize: tabletMode ? 13 : 11, fontWeight: 700 }}>
                      <span
                        style={{
                          width: tabletMode ? 9 : 7,
                          height: tabletMode ? 9 : 7,
                          borderRadius: 999,
                          background: posColor(p.position),
                          flex: "0 0 auto",
                        }}
                      />
                      <span>{p.position}</span>
                      <TeamLogo
                        team={p.team}
                        size={tabletMode ? 18 : 15}
                        fallback={<span>{formatTeamAbbreviation(p.team, "FA")}</span>}
                      />
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  </div>
) : null}


{/* Draft-board: Assign Player menu */}
{isDraft && !mobileMode && draftAssignMenu && (
  <div
    onClick={() => setDraftAssignMenu(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2000,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        left: Math.min(window.innerWidth - 220, Math.max(12, draftAssignMenu.x - 110)),
        top: Math.min(window.innerHeight - 140, Math.max(12, draftAssignMenu.y - 20)),
        width: 220,
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(20,20,24,0.92)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        padding: 10,
        backdropFilter: "blur(10px)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.85, marginBottom: 8 }}>
        {draftAssignMenu.label}
      </div>

      <button
        onClick={() => {
          setDraftAssignModal({ slotIndex: draftAssignMenu.slotIndex, label: draftAssignMenu.label });
          setDraftAssignQuery("");
          setDraftAssignMenu(null);
        }}
        style={{
          width: "100%",
          padding: "10px 10px",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.08)",
          color: "var(--text-0)",
          fontWeight: 900,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        Assign Player
      </button>
    </div>
  </div>
)}


{/* Draft-board: Remove Player menu */}
{isDraft && !mobileMode && draftRemoveMenu && (
  <div
    onClick={() => setDraftRemoveMenu(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2000,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        left: Math.min(window.innerWidth - 240, Math.max(12, draftRemoveMenu.x - 120)),
        top: Math.min(window.innerHeight - 170, Math.max(12, draftRemoveMenu.y - 20)),
        width: 240,
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(20,20,24,0.92)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        padding: 10,
        backdropFilter: "blur(10px)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.85, marginBottom: 6 }}>
        {draftRemoveMenu.label}
      </div>
      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10, lineHeight: 1.25 }}>
        {draftRemoveMenu.playerName}
      </div>

      <button
        onClick={() => {
          onToggleDrafted(draftRemoveMenu.playerId);
          setDraftRemoveMenu(null);
        }}
        style={{
          width: "100%",
          padding: "10px 10px",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(255,75,75,0.18)",
          color: "var(--text-0)",
          fontWeight: 900,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        Remove Player
      </button>
    </div>
  </div>
)}
{/* Draft-board: Assign Player modal */}
{isDraft && !mobileMode && draftAssignModal && (
  <div
    onClick={() => setDraftAssignModal(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2100,
      background: "rgba(0,0,0,0.55)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(760px, 100%)",
        maxHeight: "min(80vh, 820px)",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(18,18,22,0.96)",
        boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: 14, borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 950, opacity: 0.9 }}>Assign Player</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{draftAssignModal.label}</div>
          </div>

          <button
            onClick={() => setDraftAssignModal(null)}
            style={{
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "transparent",
              color: "var(--text-0)",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        <input
          ref={draftAssignInputRef}
          autoFocus
          value={draftAssignQuery}
          onChange={(e) => setDraftAssignQuery(e.target.value)}
          placeholder="Search players..."
          style={{
            marginTop: 10,
            width: "100%",
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--text-0)",
            outline: "none",
            fontWeight: 750,
          }}
        />
        <div style={{ fontSize: 12, opacity: 0.72, marginTop: 8 }}>
          Showing available (undrafted) players only.
        </div>
      </div>

      <div style={{ padding: 12, overflow: "auto" }}>
        {draftAssignCandidates.length === 0 ? (
          <div style={{ padding: 18, opacity: 0.8, fontWeight: 750 }}>No matching available players.</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {draftAssignCandidates.map((id: string) => {
              const p = playersById[id];
              if (!p) return null;
              return (
                <button
                  key={id}
                  onClick={() => {
                    onAssignPlayerToDraftSlot(draftAssignModal.slotIndex, id);
                    if (!draftedIds.has(id)) onToggleDrafted(id);
                    setDraftAssignModal(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 10px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "var(--text-0)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <img
                    src={p.imageUrl || "/headshot-placeholder.svg"}
                    alt={p.name}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      objectFit: "cover",
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: "rgba(255,255,255,0.35)",
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = "/headshot-placeholder.svg";
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <div style={{ fontWeight: 950, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8, fontWeight: 850 }}>{p.position}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* Draft Planner: Add Favorite modal */}
{isPlanner && plannerAssignModal && (
  <div
    onClick={() => setPlannerAssignModal(null)}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 2100,
      background: "rgba(0,0,0,0.55)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: mobileMode ? "min(92vw, 520px)" : "min(760px, 100%)",
        maxHeight: mobileMode ? "min(86vh, 760px)" : "min(80vh, 820px)",
        borderRadius: mobileMode ? 16 : 18,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(18,18,22,0.96)",
        boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: mobileMode ? 12 : 14, borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: mobileMode ? 12 : 13, fontWeight: 950, opacity: 0.9 }}>Add Player To Planner</div>
            <div style={{ fontSize: mobileMode ? 11 : 12, opacity: 0.8, marginTop: 2 }}>{plannerAssignModal.label}</div>
          </div>

          <button
            onClick={() => setPlannerAssignModal(null)}
            style={{
              padding: mobileMode ? "7px 9px" : "8px 10px",
              borderRadius: mobileMode ? 9 : 10,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "transparent",
              color: "var(--text-0)",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        <input
          ref={plannerAssignInputRef}
          autoFocus
          value={plannerAssignQuery}
          onChange={(e) => setPlannerAssignQuery(e.target.value)}
          placeholder="Search players..."
          style={{
            marginTop: 10,
            width: "100%",
            padding: mobileMode ? "9px 11px" : "10px 12px",
            borderRadius: mobileMode ? 10 : 12,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--text-0)",
            outline: "none",
            fontWeight: 750,
          }}
        />
        <div style={{ fontSize: mobileMode ? 11 : 12, opacity: 0.72, marginTop: 8 }}>
          Showing players that are not already favorited.
        </div>
      </div>

      <div style={{ padding: mobileMode ? 10 : 12, overflow: "auto" }}>
        {plannerAssignCandidates.length === 0 ? (
          <div style={{ padding: 18, opacity: 0.8, fontWeight: 750 }}>No matching players available.</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {plannerAssignCandidates.map((id: string) => {
              const p = playersById[id];
              if (!p) return null;
              return (
                <button
                  key={id}
                  onClick={() => {
                    addPlayerToPlannerRound(id, plannerAssignModal.round);
                    setPlannerAssignModal(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 10px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "var(--text-0)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <img
                    src={p.imageUrl || "/headshot-placeholder.svg"}
                    alt={p.name}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      objectFit: "cover",
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: "rgba(255,255,255,0.35)",
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = "/headshot-placeholder.svg";
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <div style={{ fontWeight: 950, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8, fontWeight: 850 }}>{p.position}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </div>
)}

      </div>
    </div>
  );
}

const MemoizedBoard = React.memo(Board);

export default MemoizedBoard;
